import React, { useEffect, useRef } from "react";
import { Animated, Easing, Image, StyleSheet, View } from "react-native";
import { AppImages } from "../../utility/AppImages";

interface ModalLikeAnimationProps {
  onComplete?: () => void;
}

const ModalLikeAnimation: React.FC<ModalLikeAnimationProps> = ({ onComplete }) => {
  // Create animated values for multiple hearts
  const heart1 = useRef({
    translateY: new Animated.Value(0),
    translateX: new Animated.Value(0),
    scale: new Animated.Value(0),
    opacity: new Animated.Value(1),
    rotate: new Animated.Value(0),
  }).current;

  const heart2 = useRef({
    translateY: new Animated.Value(0),
    translateX: new Animated.Value(0),
    scale: new Animated.Value(0),
    opacity: new Animated.Value(1),
    rotate: new Animated.Value(0),
  }).current;

  const heart3 = useRef({
    translateY: new Animated.Value(0),
    translateX: new Animated.Value(0),
    scale: new Animated.Value(0),
    opacity: new Animated.Value(1),
    rotate: new Animated.Value(0),
  }).current;

  const heart4 = useRef({
    translateY: new Animated.Value(0),
    translateX: new Animated.Value(0),
    scale: new Animated.Value(0),
    opacity: new Animated.Value(1),
    rotate: new Animated.Value(0),
  }).current;

  const heart5 = useRef({
    translateY: new Animated.Value(0),
    translateX: new Animated.Value(0),
    scale: new Animated.Value(0),
    opacity: new Animated.Value(1),
    rotate: new Animated.Value(0),
  }).current;

  useEffect(() => {
    const animateHeart = (
      heart: any,
      delay: number,
      xDirection: number,
      rotationDirection: number
    ) => {
      return Animated.sequence([
        Animated.delay(delay),
        Animated.parallel([
          // Scale up
          Animated.spring(heart.scale, {
            toValue: 1,
            friction: 3,
            tension: 100,
            useNativeDriver: true,
          }),
          // Float up
          Animated.timing(heart.translateY, {
            toValue: -120,
            duration: 1500,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
          }),
          // Move slightly left or right
          Animated.timing(heart.translateX, {
            toValue: xDirection,
            duration: 1500,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          // Rotate
          Animated.timing(heart.rotate, {
            toValue: rotationDirection,
            duration: 1500,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          // Fade out
          Animated.timing(heart.opacity, {
            toValue: 0,
            duration: 1500,
            easing: Easing.in(Easing.ease),
            useNativeDriver: true,
          }),
        ]),
      ]);
    };

    // Start animations for all hearts with different delays and directions
    Animated.parallel([
      animateHeart(heart1, 0, 20, 15),
      animateHeart(heart2, 100, -25, -20),
      animateHeart(heart3, 200, 30, 25),
      animateHeart(heart4, 300, -15, -15),
      animateHeart(heart5, 400, 10, 10),
    ]).start(() => {
      if (onComplete) {
        onComplete();
      }
    });
  }, []);

  const renderHeart = (heart: any, size: number) => {
    const rotation = heart.rotate.interpolate({
      inputRange: [-30, 30],
      outputRange: ["-30deg", "30deg"],
    });

    return (
      <Animated.View
        style={[
          styles.heartContainer,
          {
            transform: [
              { translateY: heart.translateY },
              { translateX: heart.translateX },
              { scale: heart.scale },
              { rotate: rotation },
            ],
            opacity: heart.opacity,
          },
        ]}
      >
        <Image
          source={AppImages.icLike}
          style={{ width: size, height: size }}
          resizeMode="contain"
        />
      </Animated.View>
    );
  };

  return (
    <View style={styles.container} pointerEvents="none">
      {renderHeart(heart1, 45)}
      {renderHeart(heart2, 40)}
      {renderHeart(heart3, 50)}
      {renderHeart(heart4, 38)}
      {renderHeart(heart5, 43)}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    bottom: 50,
    right: 10,
    width: 80,
    height: 150,
    alignItems: "center",
    justifyContent: "flex-end",
  },
  heartContainer: {
    position: "absolute",
    bottom: 0,
  },
});

export default ModalLikeAnimation;